import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  inject,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AMPLIFY_OUTPUTS } from '@lassi/config';
import { take } from 'rxjs';
import { NoticeComponent } from './notice.component';

@Component({
  selector: 'lib-captcha',
  imports: [NoticeComponent],
  template: `
    @if (_value) {
      <lib-notice class="success" noticeType="success" message="Success!" />
    }
    <div class="container" [class.hide]="!!_value" #captchaContainer></div>
  `,
  styles: `
    @use '@angular/material' as mat;
    @use 'theme' as theme;

    .container {
      min-height: 288px;
      max-width: 320px;
      margin: 0 auto 1rem;

      &.hide {
        display: none;
      }
    }

    .success {
      margin-bottom: 1rem;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CaptchaComponent),
      multi: true,
    },
  ],
})
export class CaptchaComponent implements ControlValueAccessor {
  private readonly amplifyOutputs$ = inject(AMPLIFY_OUTPUTS);

  protected _value: string | undefined | null;

  protected readonly captchaContainer =
    viewChild.required<ElementRef<HTMLDivElement>>('captchaContainer');

  protected set value(value: string) {
    this._value = value;
    this.onChange(value);
  }

  public writeValue(value: string | undefined | null) {
    if (value === this._value) return;

    this._value = value; // do not use this.value here because the setter calls onChange

    const elem = this.captchaContainer().nativeElement;

    this.amplifyOutputs$.pipe(take(1)).subscribe((outputs) => {
      (window as any).AwsWafCaptcha.renderCaptcha(elem, {
        apiKey: outputs.custom.captcha?.apiKey,
        onSuccess: (wafToken: string) => {
          console.log('success', wafToken);
          this.value = wafToken;
        },
        onError: (error: any) => {
          this.value = '';
          console.error(error);
        },
        dynamicWidth: true,
        defaultLocale: 'en-AU',
      });
    });
  }

  protected onChange!: (value: string | undefined | null) => void;
  protected onTouched!: () => void;

  public registerOnChange(fn: never) {
    this.onChange = fn;
  }

  public registerOnTouched(fn: never) {
    this.onTouched = fn;
  }
}
